import type {
  AssetState,
  CalculatedComponentProps,
  ConditionalMatcher,
  IncludeInDefaultAssetNameRules,
  WorkflowComponent,
  WorkflowContext,
  WorkflowStepOptions,
  WorkflowStepResponse,
} from './types';
import type Ajv from 'ajv';
import { workflowValidationSchema } from './workflowValidationSchema';

export interface WorkflowOptions {
  workflowName: string;
  workflowSteps: WorkflowStepOptions[];
}

export class WorkflowStep {
  public readonly workflowKey: string;
  public readonly workflowStepKey: string;
  public readonly workflowStepNavigationName: string;
  public readonly workflowIndex: number;
  public readonly workflowComponent: WorkflowComponent;
  public readonly excludeFromNavigation: boolean;
  public readonly calculatedComponentProps?: Record<string, CalculatedComponentProps>;
  public readonly excludeFromWorkflowStatus: boolean;
  public readonly refreshAssetsOnStepCompletion: boolean;
  public readonly workflowStepConditionalMatcher: ConditionalMatcher[];
  public readonly workflowStepConditionalValidator: Ajv.ValidateFunction;
  public readonly includeInDefaultAssetNameRules?: IncludeInDefaultAssetNameRules;
  public readonly optional: boolean;
  public skipped?: boolean;
  constructor(workflowKey: string, options: WorkflowStepOptions, assetState: AssetState) {
    this.optional = !!options.optional;
    this.workflowKey = workflowKey;
    this.workflowStepKey = options.workflowStepKey;
    this.workflowStepNavigationName = options.workflowStepNavigationName;
    this.workflowIndex = options.workflowStepIndex || 0;
    this.calculatedComponentProps = options.calculatedComponentProps;
    this.workflowComponent = options.workflowComponent;
    this.excludeFromNavigation = options.excludeFromNavigation === undefined ? false : options.excludeFromNavigation;
    this.excludeFromWorkflowStatus =
      options.excludeFromWorkflowStatus === undefined ? false : options.excludeFromWorkflowStatus;
    this.refreshAssetsOnStepCompletion =
      options.refreshAssetsOnStepCompletion === undefined ? false : options.refreshAssetsOnStepCompletion;
    this.workflowStepConditionalMatcher = options.workflowStepConditionalMatcher || [];
    this.workflowStepConditionalValidator = workflowValidationSchema.compile(
      this.workflowStepConditionalMatcher,
      this.workflowKey,
      assetState,
    );
    this.includeInDefaultAssetNameRules = options.includeInDefaultAssetNameRules;
  }
  public setSkipped = (skipped: boolean) => {
    this.skipped = true;
    return this;
  };
  public validate(context: WorkflowContext) {
    return this.workflowStepConditionalValidator(context);
  }

  public isSummaryStep() {
    return this.workflowComponent.workflowComponentType === 'workflowSummary';
  }
  public calculateComponentProps(context: WorkflowContext) {
    return {
      ...this.workflowComponent,
      ...(this.calculatedComponentProps
        ? Object.entries(this.calculatedComponentProps).reduce<Record<string, unknown>>((prev, curr) => {
            const transform = curr[1]?.transformFn ? curr[1]?.transformFn : (p: any) => p;
            prev[curr[0]] = transform(context.workflowState[context.workflowKey][curr[1].property]);
            return prev;
          }, {})
        : {}),
    };
  }

  public isComplete(context: WorkflowContext) {
    const state = context.workflowState[context.workflowKey] || {};
    return this.skipped || state[this.workflowStepKey] !== undefined;
  }

  public build(context: WorkflowContext): WorkflowStepResponse {
    if (!this.validate(context)) {
      throw new Error('Workflow Step Validation Error');
    }
    return {
      optional: this.optional,
      workflowStepKey: this.workflowStepKey,
      workflowComponent: this.calculateComponentProps(context),
      workflowContext: context,
      refreshAssetsOnStepCompletion: this.refreshAssetsOnStepCompletion,
    };
  }
}
